import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Slide,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateRight as RotateIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

interface MediaViewerProps {
  open: boolean;
  onClose: () => void;
  mediaUrl: string;
  mediaType: 'image' | 'video' | 'document' | 'audio' | string;
  filename?: string;
  mimeType?: string;
}

// Slide up transition for mobile feel
const SlideTransition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const MediaViewer: React.FC<MediaViewerProps> = ({
  open,
  onClose,
  mediaUrl,
  mediaType,
  filename,
  mimeType,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  // Reset state when media changes
  useEffect(() => {
    if (open) {
      setZoom(1);
      setRotation(0);
      setPosition({ x: 0, y: 0 });
      setLoading(true);
      setError(false);
    }
  }, [open, mediaUrl]);

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === '+' || e.key === '=') handleZoomIn();
      if (e.key === '-') handleZoomOut();
      if (e.key === 'r' || e.key === 'R') handleRotate();
    };

    if (open) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, onClose]);

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.5, 4));
  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.5, 0.5));
    if (zoom <= 1) setPosition({ x: 0, y: 0 });
  };
  const handleRotate = () => setRotation((prev) => (prev + 90) % 360);

  const handleDownload = async () => {
    try {
      const response = await fetch(mediaUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `download-${Date.now()}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
      // Fallback: open in new tab
      window.open(mediaUrl, '_blank');
    }
  };

  const handleOpenInNewTab = () => {
    window.open(mediaUrl, '_blank');
  };

  // Touch/drag handlers for panning zoomed images
  const handlePointerDown = (e: React.PointerEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        posX: position.x,
        posY: position.y,
      };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (isDragging && zoom > 1) {
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      setPosition({
        x: dragStart.current.posX + dx,
        y: dragStart.current.posY + dy,
      });
    }
  };

  const handlePointerUp = () => {
    setIsDragging(false);
  };

  // Double tap to zoom
  const lastTap = useRef(0);
  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTap.current < 300) {
      if (zoom > 1) {
        setZoom(1);
        setPosition({ x: 0, y: 0 });
      } else {
        setZoom(2);
      }
    }
    lastTap.current = now;
  };

  const isImage = mediaType === 'image' || mimeType?.startsWith('image/');
  const isVideo = mediaType === 'video' || mimeType?.startsWith('video/');

  const renderContent = () => {
    if (error) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'white',
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" gutterBottom>
            Unable to load media
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
            The file might be unavailable or in an unsupported format.
          </Typography>
          <IconButton
            onClick={handleDownload}
            sx={{ color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}
          >
            <DownloadIcon />
          </IconButton>
        </Box>
      );
    }

    if (isImage) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            overflow: 'hidden',
            cursor: zoom > 1 ? 'grab' : 'default',
            touchAction: 'none',
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onClick={handleDoubleTap}
        >
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress sx={{ color: 'white' }} />
            </Box>
          )}
          <img
            src={mediaUrl}
            alt={filename || 'Image'}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setError(true);
            }}
            draggable={false}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              transform: `scale(${zoom}) rotate(${rotation}deg) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
              transition: isDragging ? 'none' : 'transform 0.2s ease',
              opacity: loading ? 0 : 1,
              userSelect: 'none',
            }}
          />
        </Box>
      );
    }

    if (isVideo) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            p: 2,
          }}
        >
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress sx={{ color: 'white' }} />
            </Box>
          )}
          <video
            src={mediaUrl}
            controls
            autoPlay
            playsInline
            onLoadedData={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setError(true);
            }}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              borderRadius: 8,
              opacity: loading ? 0 : 1,
            }}
          />
        </Box>
      );
    }

    // For documents and other types - show download prompt
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'white',
          p: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" gutterBottom>
          {filename || 'Document'}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.7, mb: 3 }}>
          This file type cannot be previewed in the app.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <IconButton
            onClick={handleDownload}
            sx={{
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              p: 2,
            }}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            onClick={handleOpenInNewTab}
            sx={{
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              p: 2,
            }}
          >
            <OpenInNewIcon />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      TransitionComponent={SlideTransition}
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          backgroundImage: 'none',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          px: 2,
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(8px)',
          zIndex: 10,
        }}
      >
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>

        <Typography
          variant="body2"
          sx={{
            color: 'white',
            flex: 1,
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            px: 2,
          }}
        >
          {filename || ''}
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {isImage && (
            <>
              <IconButton onClick={handleZoomOut} sx={{ color: 'white' }} disabled={zoom <= 0.5}>
                <ZoomOutIcon />
              </IconButton>
              <IconButton onClick={handleZoomIn} sx={{ color: 'white' }} disabled={zoom >= 4}>
                <ZoomInIcon />
              </IconButton>
              <IconButton onClick={handleRotate} sx={{ color: 'white' }}>
                <RotateIcon />
              </IconButton>
            </>
          )}
          <IconButton onClick={handleDownload} sx={{ color: 'white' }}>
            <DownloadIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ height: '100%', pt: 7, pb: 2 }}>{renderContent()}</Box>

      {/* Footer - zoom indicator for images */}
      {isImage && zoom !== 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            px: 2,
            py: 0.5,
            borderRadius: 2,
          }}
        >
          <Typography variant="caption" sx={{ color: 'white' }}>
            {Math.round(zoom * 100)}%
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default MediaViewer;
